import { Stack, Link } from "@mui/material";

import { format } from "date-fns";

import { Dispatch, SetStateAction } from "react";

import { TableColumn } from "@/components/Table/Table";

import { ExternalLink } from "@/components/ExternalLink/ExternalLink";

import { SortParams } from "@/types/types";
import { ensureHttpProtocol } from "@/utils/url";

import { AssignStatus } from "@/enum/list.enum";

import { ListPerson } from "../../schema/list";

import {
	renderEstimatedNumEmployeesColumn,
	renderFoundedYearColumn,
	renderPersonCityColumn,
	renderPersonStateColumn,
	renderPersonCountryColumn,
	renderCompanyCityColumn,
	renderCompanyStateColumn,
	renderCompanyCountryColumn,
	renderLanguageColumn,
	renderConnectionsColumn,
	renderGenderColumn,
	renderIndustryColumn,
	renderSeniorityColumn,
	renderTitleColumn,
	renderJobsCompanyColumn,
	renderCompanyColumn,
	renderAssignToColumn,
	renderSelectColumn,
	renderFollowers,
	renderNameColumn,
	renderJobsNameColumn,
	renderJobCountryColumn,
	renderJobCityColumn,
	renderSourceNameColumn,
	renderCompanyPeopleCheckColumn,
	renderAboutColumn,
	renderHeadlineColumn,
	renderCreatedAt,
	renderJobGenderColumn,
	renderSalutationDeColumn,
	renderSalutationEnColumn,
	renderJobSalutationDeColumn,
	renderJobSalutationEnColumn,
	renderExclusionReasonColumn,
	renderPreviousEmployersColumn,
	renderCompanyCategoryColumn,
	renderCompanyScoreColumn,
	renderPersonaCategoryColumn,
	renderPersonaScoreColumn,
	renderReportetEmployeesColumn,
	renderRoleChangeColumn,
	renderPreviousEmployerColumn,
	renderPreviousJobTitleColumn,
	renderCampaignAddedAt,
	renderAssignedStatusColumn,
	renderLastNodeTypeColumn,
	renderLastScheduledAtColumn,
	renderLastMessageColumn,
	renderDeepResearchStatusColumn,
} from "../../utils/allColumns";
import {
	renderCompanyOtherListsColumn,
	renderOtherListsColumn,
} from "../../utils/otherListsColumns";
import { renderMaybeColumn } from "../../utils/renderMaybeColumn";
import { GetIrlColumns } from "../CustomerImplementations/IrlColumns";

export interface JobTableColumnsProps {
	selectAll: boolean;
	setShowAll: Dispatch<SetStateAction<boolean>>;
	toggleSelectAll: () => void;
	selectedItems: number[];
	handleSelectItem: (id: number) => void;
	handleCopyClick: (value: string) => void;
	ts: (key: string) => string;
	sortingInbox: SortParams;
	setShowEdit: ({ id, name }: { id: number; name: string }) => void;
	showEdit: { id: number; name: string } | undefined;
	parts: string[];
	integrations: { [key: string]: boolean | undefined };
	handleSelectCampaignAndProspect: (
		id: number | string,
		listPersonId: number,
		type?: "internal" | "lemlist"
	) => void;
	assignStatus: AssignStatus;
	onResearchClick?: (row: ListPerson) => void;
	onOpenDetail?: (row: ListPerson) => void;
}

export const JobTableColumns = ({
	selectAll,
	setShowAll,
	toggleSelectAll,
	selectedItems,
	handleSelectItem,
	handleCopyClick,
	ts,
	sortingInbox,
	setShowEdit,
	showEdit,
	parts,
	integrations,
	handleSelectCampaignAndProspect,
	assignStatus,
	onResearchClick,
	onOpenDetail,
}: JobTableColumnsProps): TableColumn[] => {
	const columns: TableColumn[] = [
		renderSelectColumn(setShowAll, toggleSelectAll, selectAll, selectedItems, handleSelectItem),
		renderMaybeColumn(ts),
		renderAssignToColumn(ts, handleCopyClick, handleSelectCampaignAndProspect),
	];

	if (parts.includes("managingDirector")) {
		columns.push(renderJobsNameColumn(ts, handleCopyClick, sortingInbox, setShowEdit, showEdit));
		columns.push(
			renderJobsCompanyColumn(ts, handleCopyClick, sortingInbox, setShowEdit, showEdit, true)
		);
		columns.push(renderExclusionReasonColumn(ts, sortingInbox));
		columns.push(renderJobGenderColumn(ts, handleCopyClick, sortingInbox));
		columns.push(renderJobSalutationDeColumn(ts));
		columns.push(renderJobSalutationEnColumn(ts));
	} else if (parts.includes("prospect")) {
		columns.push(renderNameColumn(ts, handleCopyClick, sortingInbox, onOpenDetail));
		columns.push(renderCompanyColumn(ts, handleCopyClick, sortingInbox, setShowEdit, showEdit));
		columns.push(renderExclusionReasonColumn(ts, sortingInbox));
		columns.push(renderSalutationDeColumn(ts));
		columns.push(renderSalutationEnColumn(ts));
	}

	if (!parts.includes("prospect") && !parts.includes("managingDirector")) {
		columns.push(
			renderJobsCompanyColumn(
				ts,
				handleCopyClick,
				sortingInbox,
				setShowEdit,
				showEdit,
				parts.includes("managingDirector")
			)
		);
	}

	if (!parts.includes("managingDirector") && !parts.includes("prospect")) {
		columns.push(renderJobCountryColumn(ts, sortingInbox));
		columns.push(renderJobCityColumn(ts, sortingInbox));
	}

	if (parts.includes("prospect")) {
		columns.push(renderCompanyPeopleCheckColumn(ts));
		columns.push(renderCompanyOtherListsColumn(ts, sortingInbox));
	}

	columns.push(renderDeepResearchStatusColumn(onResearchClick));

	columns.push(
		...[
			{
				title: ts("jobTitle"),
				width: "fit-content",
				orderTitle: "offer.jobTitle",
				uid: "jobTitle",
				hideable: true,
				moveable: true,
				show: true,
				render: (row: ListPerson) => (
					<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
						<Stack alignItems="flex-start" direction="row" justifyContent="flex-start">
							<Link href={ensureHttpProtocol(row.offer?.offerLink ?? "#")} target="_blank">
								<ExternalLink />
							</Link>
							<button
								className="text-ellipsis overflow-hidden max-w-[250px] cursor-pointer"
								onClick={() => handleCopyClick(row.offer?.jobTitle ?? "")}
							>
								{row.offer?.jobTitle}
							</button>
						</Stack>
					</Stack>
				),
			},
			{
				title: ts("postedAt"),
				width: "fit-content",
				orderTitle: "offer.postedAt",
				uid: "postedAt",
				hideable: true,
				moveable: true,
				show: true,
				render: (row: ListPerson) => (
					<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
						<Stack alignItems="flex-start" direction="row" justifyContent="flex-start">
							<button
								className="text-ellipsis overflow-hidden max-w-[250px] cursor-pointer"
								onClick={() =>
									row.offer?.postedAt && handleCopyClick(row.offer?.postedAt.toISOString())
								}
							>
								{row.offer?.postedAt && format(new Date(row.offer?.postedAt), "dd.MM.yyyy")}
							</button>
						</Stack>
					</Stack>
				),
			},
			{
				title: ts("employmentType"),
				width: "fit-content",
				orderTitle: "offer.employmentType",
				uid: "employmentType",
				hideable: true,
				moveable: true,
				show: true,
				render: (row: ListPerson) => (
					<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
						<Stack alignItems="flex-start" direction="column" justifyContent="flex-start">
							<button
								className="text-ellipsis overflow-hidden max-w-[250px] cursor-pointer"
								onClick={() => handleCopyClick(row.offer?.employmentType ?? "")}
							>
								{row.offer?.employmentType}
							</button>
						</Stack>
					</Stack>
				),
			},
			renderOtherListsColumn(ts, sortingInbox),
		]
	);

	if (parts.includes("prospect")) {
		columns.push(
			...[
				renderGenderColumn(ts, handleCopyClick, sortingInbox, setShowEdit, showEdit),
				renderTitleColumn(ts, handleCopyClick, sortingInbox),
				renderHeadlineColumn(ts, handleCopyClick, sortingInbox),
				renderAboutColumn(ts, handleCopyClick, sortingInbox),
				renderSeniorityColumn(ts, handleCopyClick, sortingInbox),
				renderConnectionsColumn(ts, sortingInbox),
				renderIndustryColumn(ts, handleCopyClick, sortingInbox),
				renderEstimatedNumEmployeesColumn(ts, handleCopyClick, sortingInbox),
				renderReportetEmployeesColumn(ts, handleCopyClick, sortingInbox),
				renderFoundedYearColumn(ts, handleCopyClick, sortingInbox),
				renderPersonCityColumn(ts, handleCopyClick, sortingInbox),
				renderPersonStateColumn(ts, handleCopyClick, sortingInbox),
				renderPersonCountryColumn(ts, handleCopyClick, sortingInbox),
				renderCompanyCityColumn(ts, handleCopyClick, sortingInbox),
				renderCompanyStateColumn(ts, handleCopyClick, sortingInbox),
				renderCompanyCountryColumn(ts, handleCopyClick, sortingInbox),
				renderLanguageColumn(ts, sortingInbox),
				renderFollowers(ts, sortingInbox),
				renderPreviousEmployersColumn(ts),
				renderPreviousEmployerColumn(ts),
				renderPreviousJobTitleColumn(ts),
			]
		);

		if (integrations.IRL_COUNTRIES_SUM) {
			const irlColumns = GetIrlColumns(ts);

			columns.push(...irlColumns);
		}
	}

	if (assignStatus === AssignStatus.ASSIGNED) {
		columns.push(renderCampaignAddedAt(ts, sortingInbox));
		columns.push(renderAssignedStatusColumn(ts, sortingInbox));
		columns.push(renderLastNodeTypeColumn(ts, sortingInbox));
		columns.push(renderLastScheduledAtColumn(ts, sortingInbox));
		columns.push(renderLastMessageColumn(ts));
	}

	if (integrations.LEAD_SCORING) {
		columns.push(renderPersonaCategoryColumn(sortingInbox));
		columns.push(renderCompanyCategoryColumn(sortingInbox));
		columns.push(renderPersonaScoreColumn(sortingInbox));
		columns.push(renderCompanyScoreColumn(sortingInbox));
	}

	columns.push(renderSourceNameColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderCreatedAt(ts, sortingInbox));

	columns.push(renderRoleChangeColumn());

	return columns;
};
